import clsx from 'clsx';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { FieldHelp } from '../../../components/FieldHelp';

import {
  FIELD_TYPE_OPTIONS,
  FieldTypes,
  VALIDATION_RULE_OPTIONS,
  type FieldDefinitionRequest,
  type FieldType,
  type ValidationRule,
} from '../api';
import type { FieldDefinitionDraft } from '../hooks/useFieldDefinitionState';
import { emptyGroupChild } from '../hooks/useFieldDefinitionState';
import { GroupChildrenEditor } from './GroupChildrenEditor';
import { TableColumnEditor } from './TableColumnEditor';

interface FieldRowProps {
  field: FieldDefinitionDraft;
  index: number;
  onChange: (draftId: string, patch: Partial<Omit<FieldDefinitionRequest, 'children'>>) => void;
  onRemove: (draftId: string) => void;
  /** Replace the children array (used when the type switches to/from Group). */
  onSetChildren: (draftId: string, children: FieldDefinitionDraft[] | null) => void;
  onChildAdd: (parentDraftId: string) => void;
  onChildRemove: (parentDraftId: string, childDraftId: string) => void;
  onChildUpdate: (
    parentDraftId: string,
    childDraftId: string,
    patch: Partial<Omit<FieldDefinitionRequest, 'children'>>,
  ) => void;
  /** Per-field error map (keyed by `name` so children look up `nameError`, etc.). */
  errors?: Partial<Record<keyof FieldDefinitionRequest, string>>;
  disabled?: boolean;
}

/**
 * Sortable card representing a single field in the editor.
 * Drag handle is the left grip — the rest of the surface is interactive.
 */
export function FieldRow({
  field,
  index,
  onChange,
  onRemove,
  onSetChildren,
  onChildAdd,
  onChildRemove,
  onChildUpdate,
  errors,
  disabled = false,
}: FieldRowProps): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.draftId,
    disabled,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={clsx(
        'card flex items-stretch gap-3 p-3',
        isDragging && 'ring-2 ring-primary',
      )}
    >
      <button
        type="button"
        className="flex w-6 cursor-grab touch-none items-center justify-center text-slate-400 hover:text-slate-700 active:cursor-grabbing"
        aria-label={`Reordenar campo ${field.name === '' ? `#${index + 1}` : field.name}`}
        {...attributes}
        {...listeners}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
          <circle cx="9" cy="6" r="1.5" />
          <circle cx="15" cy="6" r="1.5" />
          <circle cx="9" cy="12" r="1.5" />
          <circle cx="15" cy="12" r="1.5" />
          <circle cx="9" cy="18" r="1.5" />
          <circle cx="15" cy="18" r="1.5" />
        </svg>
      </button>

      <div className="grid flex-1 grid-cols-1 gap-3 md:grid-cols-12">
        <div className="md:col-span-4">
          <label className="label" htmlFor={`field-name-${field.draftId}`}>
            Nombre
          </label>
          <input
            id={`field-name-${field.draftId}`}
            className={clsx('input', errors?.name !== undefined && 'border-danger')}
            type="text"
            value={field.name}
            placeholder="ej. iban_destinatario"
            onChange={(event) => onChange(field.draftId, { name: event.target.value })}
            disabled={disabled}
          />
          {errors?.name !== undefined ? (
            <p className="mt-1 text-xs text-danger">{errors.name}</p>
          ) : null}
        </div>

        <div className="md:col-span-2">
          <label className="label flex items-center gap-1" htmlFor={`field-type-${field.draftId}`}>
            Tipo
            <FieldHelp label="Tipo" text="Tipo primitivo al que se convierte el valor extraído: texto, número (decimal/entero), fecha, booleano, tabla (filas con columnas) o grupo (sub-campos)." />
          </label>
          <select
            id={`field-type-${field.draftId}`}
            className="input"
            value={field.type}
            onChange={(event) => {
              const nextType = event.target.value as FieldType;
              // Switching to Table seeds an empty columns array so the sub-editor renders
              // with the "add first column" empty state. Switching away from Table clears
              // any column data — the API validator rejects Columns on non-Table types.
              const isTable = nextType === FieldTypes.Table;
              const wasTable = field.type === FieldTypes.Table;
              const isGroup = nextType === FieldTypes.Group;
              const wasGroup = field.type === FieldTypes.Group;
              const patch: Partial<Omit<FieldDefinitionRequest, 'children'>> = { type: nextType };
              if (isTable && !wasTable) {
                patch.columns = [];
                patch.pattern = null;
                patch.validationRule = 'None';
              } else if (!isTable && wasTable) {
                patch.columns = null;
              }
              if (isGroup && !wasGroup) {
                // Seed a single empty child so the editor renders with a starting row.
                // Group fields don't carry pattern/validation/columns.
                patch.pattern = null;
                patch.validationRule = 'None';
                patch.columns = null;
                onChange(field.draftId, patch);
                onSetChildren(field.draftId, [emptyGroupChild()]);
                return;
              }
              if (!isGroup && wasGroup) {
                onSetChildren(field.draftId, null);
              }
              onChange(field.draftId, patch);
            }}
            disabled={disabled}
          >
            {FIELD_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2 flex items-end gap-3">
          <label className="flex items-center gap-1.5 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={field.required}
              onChange={(event) => onChange(field.draftId, { required: event.target.checked })}
              disabled={disabled}
            />
            Obligatorio
            <FieldHelp label="Obligatorio" text="Si se marca, el documento se considera inválido cuando este campo no aparece en el resultado." />
          </label>
          <label className="flex items-center gap-1.5 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={field.critical}
              onChange={(event) => onChange(field.draftId, { critical: event.target.checked })}
              disabled={disabled}
            />
            Crítico
            <FieldHelp label="Crítico" text="Si se marca, una confianza baja en este campo envía todo el documento a revisión manual." />
          </label>
        </div>

        {field.type === FieldTypes.Table ? (
          <div className="md:col-span-4 flex items-end text-xs text-slate-500">
            <span>
              Las tablas declaran su esquema mediante columnas (abajo). La validación regex se aplica
              por columna en la sección de columnas.
            </span>
          </div>
        ) : field.type === FieldTypes.Group ? (
          <div className="md:col-span-4 flex items-end text-xs text-slate-500">
            <span>
              Los grupos contienen sub-campos tipados (abajo). Cada sub-campo declara su propia
              regla de validación y patrón regex.
            </span>
          </div>
        ) : (
          <>
            <div className="md:col-span-2">
              <label className="label flex items-center gap-1" htmlFor={`field-rule-${field.draftId}`}>
                Validación
                <FieldHelp label="Validación" text="Regla semántica integrada que se aplica sobre el valor (p. ej. checksum de IBAN o NIF, o suma de hijos). 'None' la desactiva." />
              </label>
              <select
                id={`field-rule-${field.draftId}`}
                className="input"
                value={field.validationRule}
                onChange={(event) =>
                  onChange(field.draftId, { validationRule: event.target.value as ValidationRule })
                }
                disabled={disabled}
              >
                {VALIDATION_RULE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="label flex items-center gap-1" htmlFor={`field-pattern-${field.draftId}`}>
                Regex
                <FieldHelp label="Regex" text="Patrón opcional que el valor debe cumplir. Se compara contra la forma invariante del valor (un entero 58 se valida como «58», una fecha como «2026-05-15»)." />
              </label>
              <input
                id={`field-pattern-${field.draftId}`}
                className="input font-mono text-xs"
                type="text"
                value={field.pattern ?? ''}
                placeholder="opcional"
                onChange={(event) =>
                  onChange(field.draftId, {
                    pattern: event.target.value === '' ? null : event.target.value,
                  })
                }
                disabled={disabled}
              />
            </div>
          </>
        )}

        <div className="md:col-span-12">
          <label className="label flex items-center justify-between" htmlFor={`field-hint-${field.draftId}`}>
            <span className="flex items-center gap-1">
              Pista para la IA (opcional)
              <FieldHelp label="Pista para la IA" text="Texto libre que guía al modelo para localizar e interpretar el campo (p. ej. «el IBAN aparece junto al logo del banco»). Es orientación, no una regla de validación." />
            </span>
            <span className="text-xs font-normal text-slate-400">
              {(field.hint ?? '').length}/500
            </span>
          </label>
          <textarea
            id={`field-hint-${field.draftId}`}
            className="input min-h-[60px] resize-y text-xs"
            rows={2}
            maxLength={1000}
            value={field.hint ?? ''}
            placeholder='ej. "este IBAN suele aparecer en la última página, cerca del logo del banco"'
            onChange={(event) =>
              onChange(field.draftId, {
                hint: event.target.value === '' ? null : event.target.value,
              })
            }
            disabled={disabled}
          />
        </div>

        {field.type === FieldTypes.Table ? (
          <div className="md:col-span-12">
            <TableColumnEditor
              columns={field.columns ?? []}
              onChange={(next) => onChange(field.draftId, { columns: next })}
              disabled={disabled}
            />
          </div>
        ) : null}

        {field.type === FieldTypes.Group ? (
          <div className="md:col-span-12">
            <GroupChildrenEditor
              parentDraftId={field.draftId}
              children={field.children ?? []}
              onAdd={onChildAdd}
              onRemove={onChildRemove}
              onUpdate={onChildUpdate}
              disabled={disabled}
            />
          </div>
        ) : null}
      </div>

      <button
        type="button"
        onClick={() => onRemove(field.draftId)}
        className="flex w-8 items-center justify-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-danger"
        aria-label={`Eliminar campo ${field.name === '' ? `#${index + 1}` : field.name}`}
        disabled={disabled}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
          <path d="M3 6h18M8 6V4h8v2m-1 0v14H9V6" />
        </svg>
      </button>
    </div>
  );
}
